import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';
import { type AreEqual, assertTypes } from '../primitives';

export const posPriceAdjustmentShortcutSchema = z.object({
  uuid: z.string().default(() => uuidv4()),
  /**
   * Optional short uppercase shorthand shown on the POS button, and snapshotted onto the order
   * as its price adjustment reason (e.g. "STAFF", "SUNDAY"). Without one the shortcut reads as
   * the percentage alone (e.g. "-15%") and the order carries no reason.
   */
  reason: z.string().nullable().default(null),
  /** Whether tapping the shortcut discounts or surcharges the order. */
  type: z.literal(['DISCOUNT', 'SURCHARGE']).default('DISCOUNT'),
  /** Whole percent (1-100) applied to the order total. */
  percent: z.number().nullable().default(null),
  /**
   * Position of the shortcut's button on the POS, set by rearranging them in the admin.
   * Null until the first save assigns one, which sorts the shortcut last.
   */
  displayIndex: z.number().nullable().default(null),
});
export type PosPriceAdjustmentShortcut = z.output<typeof posPriceAdjustmentShortcutSchema>;

export const posPriceAdjustmentShortcutStrictSchema = posPriceAdjustmentShortcutSchema
  .safeExtend({
    reason: z
      .string()
      .trim()
      .max(8, 'Max 8 characters')
      .toUpperCase()
      .nullable()
      /** A blank field means "no reason", so it is stored as null rather than an empty string */
      .transform((reason) => (reason === '' ? null : reason)),
    percent: z.number().int().min(1).max(100).nullable(),
    displayIndex: z.int().nullable(),
  })
  /** Validate: percent must be set */
  .refine((shortcut) => shortcut.percent !== null, {
    message: 'Percent is required',
    path: ['percent'],
  });
/** The strict schema should not change the output types */
assertTypes<
  AreEqual<PosPriceAdjustmentShortcut, z.infer<typeof posPriceAdjustmentShortcutStrictSchema>>
>();
